/**
 * "Últimos focos": lista dos focos mais recentes (mais novos primeiro).
 * Desktop: conteúdo do inspetor encaixado à direita (`ui/Inspector`; a escolha
 * aberto/fechado fica guardada no estado da interface). Celular: botão abaixo
 * dos botões do mapa, que abre uma folha inferior.
 *
 * Leve: a lista só existe com o painel aberto e é recalculada só quando o
 * conjunto de focos muda (auto refresh); os mais novos são os últimos índices
 * do conjunto (já ordenado), sem varrer os dados. O filtro por áreas (até 3,
 * `AreaFilter`) separa os focos das áreas uma vez por conjunto; o card
 * inferior conta os focos do período do loop por busca binária. Ao escolher um foco, o mapa
 * voa até ele, marca o ponto e abre o popup do foco.
 */
import clsx from 'clsx';
import { Flame, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type OlMap from 'ol/Map';
import { useShallow } from 'zustand/react/shallow';
import { FirePopup } from '../fires/FirePopup';
import { areaDoFocoNaTela } from '../fires/areaDoFoco';
import { countWindow, GOES_SATELLITE, type FireDataset } from '../fires/dataset';
import { formatDecimal } from '../fires/format';
import { estimateMunicipioNow } from '../fires/municipio';
import { fireVisibility, frameIndexForFire, recentFires, RECENT_LIMIT, type RecentFire } from '../fires/recent';
import { useFireStore, type AreaFilterStatus } from '../fires/store';
import { displayedSnapshot, loopPeriod, windowFor } from '../fires/timeFilter';
import type { DisplayMode } from '../fires/types';
import { retryAreaFilter } from '../fires/areaSync';
import { formatInt, formatLonLat } from '../lib/format';
import { formatDayTimeBrt, formatRelative, formatTimeBrt } from '../lib/time';
import { goToFrame } from '../loop/playback';
import { NATIVE_RESOLUTION } from '../map/createMap';
import { useMapInstance } from '../map/mapContext';
import { openPopupAt } from '../map/popupControl';
import { useTimeStore } from '../state/time';
import { useVectorStore } from '../vectors/store';
import { AreaFilter } from './AreaFilter';
import { useNow } from './hooks';
import { useModalLayer } from './modalStack';
import { Button, IconButton } from './primitives';

// ---------------------------------------------------------------------------
// Ir até o foco
// ---------------------------------------------------------------------------

/** Popup do foco escolhido na lista, com aviso quando ele não aparece na imagem exibida. */
function RecentFirePopup(props: { item: RecentFire }) {
  const { fire } = props.item;
  const mode = useFireStore((s) => s.mode);
  // Só texto/número: re-renderiza quando a situação muda, não a cada quadro.
  const where = useTimeStore((s) => {
    const snap = displayedSnapshot(s);
    const vis = fireVisibility(fire.t, windowFor(mode, snap));
    if (vis === 'visible' || vis === 'unknown') return vis;
    const i = frameIndexForFire(snap.frames, fire.t, loopPeriod(snap.frames, snap.range)?.from ?? null);
    return i >= 0 ? i : vis;
  });

  let note = null;
  if (typeof where === 'number') {
    const frameT = useTimeStore.getState().frames[where];
    note = (
      <>
        Este foco não aparece na imagem exibida.{' '}
        <button
          type="button"
          onClick={() => goToFrame(where)}
          className="font-semibold text-mata-300 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-mata-400"
        >
          Ir para a imagem das {frameT !== undefined ? formatTimeBrt(frameT) : '—'}
        </button>
      </>
    );
  } else if (where === 'later') {
    note = <>Detectado depois da última imagem do loop: ainda não aparece no mapa.</>;
  } else if (where === 'earlier') {
    note = <>Detectado antes do período do loop: não aparece no mapa.</>;
  }

  return (
    <div>
      <FirePopup fire={fire} />
      {note && (
        <p className="mt-2 rounded-lg bg-ink-800 px-2 py-1.5 text-[11px] leading-snug text-ink-200" role="note">
          {note}
        </p>
      )}
    </div>
  );
}

/**
 * Voa até o foco (sem afastar se já estiver mais perto), marca o ponto e abre
 * o popup. O inspetor é encaixado (fora do mapa), então não há painel a
 * descontar: só o popup, que abre abaixo do ponto.
 */
function goToFire(map: OlMap, item: RecentFire): void {
  const view = map.getView();
  const current = view.getResolution() ?? NATIVE_RESOLUTION;
  const resolution = view.getConstrainedResolution(Math.min(current, NATIVE_RESOLUTION * 1.5)) ?? current;
  // A ficha abre abaixo do foco: o foco fica acima do centro, e ela cabe embaixo.
  const center = view.getConstrainedCenter([item.x, item.y - 150 * resolution], resolution);
  const open = () =>
    openPopupAt([item.x, item.y], <RecentFirePopup item={item} />, {
      marker: true,
      abaixo: true,
      // Como no clique no mapa: a ficha não cobre o foco nem o círculo de 375 m.
      movel: true,
      evitar: areaDoFocoNaTela(item.fire.lat, item.fire.lon) ?? undefined,
    });
  view.cancelAnimations();
  // Só abre se o voo terminou (o usuário pode interrompê-lo arrastando o mapa).
  view.animate({ center, resolution, duration: 650 }, (complete) => {
    if (complete) open();
  });
}

// ---------------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------------

function satelliteShort(sat: string): string {
  return sat === GOES_SATELLITE ? 'GOES-19' : sat;
}

function RecentFireList(props: { dataset: FireDataset; areas: AreaFilterStatus; onPick: (item: RecentFire) => void; className?: string }) {
  const { dataset, areas } = props;
  const sources = useFireStore((s) => s.sources);
  const style = useFireStore((s) => s.style);
  const loading = useFireStore((s) => s.status.goes10min.state === 'loading' || s.status.diario.state === 'loading');
  // Municípios já em memória permitem estimar o município dos focos de 10 min (nunca baixa daqui).
  const municipiosReady = useVectorStore((s) => s.data.municipios?.status === 'ready');
  const now = useNow(60_000);
  const filtered = areas.state === 'ready';

  const items = useMemo(() => {
    void municipiosReady;
    // Com filtro, o conjunto já tem só os focos das áreas (areaSync.ts).
    return recentFires(dataset, RECENT_LIMIT).map((item) => {
      const f = item.fire;
      let place = f.municipio ? `${f.municipio}${f.estado ? `/${f.estado}` : ''}` : null;
      let estimated = false;
      if (!place) {
        const m = estimateMunicipioNow(item.x, item.y);
        if (m) {
          place = `${m.nome}${m.uf ? `/${m.uf}` : ''}`;
          estimated = true;
        }
      }
      return { item, place, estimated };
    });
  }, [dataset, municipiosReady]);

  if (!sources.goes10min && !sources.diario) {
    return <p className="px-3 py-4 text-sm text-ink-300">Os focos de queimada estão desligados (aba Queimadas).</p>;
  }
  if (areas.state === 'loading' && items.length === 0) {
    return <p className="px-3 py-4 text-sm text-ink-300">Carregando os limites das áreas…</p>;
  }
  if (areas.state === 'error') {
    return (
      <div className="space-y-2 px-3 py-4 text-sm text-ink-300" role="alert">
        <p>Não foi possível baixar os limites das áreas do filtro. Enquanto isso, o mapa mostra todos os focos.</p>
        <Button size="sm" onClick={retryAreaFilter}>
          Tentar de novo
        </Button>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <p className="px-3 py-4 text-sm text-ink-300">
        {loading ? 'Carregando focos…' : filtered ? 'Nenhum foco nas áreas do filtro no período carregado.' : 'Nenhum foco no período carregado.'}
      </p>
    );
  }

  return (
    <ul className={clsx('divide-y divide-ink-700/60', props.className)} data-testid="recent-fires-list">
      {items.map(({ item, place, estimated }) => {
        const f = item.fire;
        const color = f.satelite === GOES_SATELLITE ? style.colorGoes : style.colorOthers;
        return (
          <li key={`${f.id}-${item.index}`}>
            <button
              type="button"
              onClick={() => props.onPick(item)}
              className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-ink-700/60 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 pointer-coarse:py-2.5"
            >
              <span
                className="mt-1 inline-block size-2.5 shrink-0 rounded-full"
                style={{ background: color, boxShadow: `0 0 6px 1px ${color}88` }}
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline gap-1.5 text-xs">
                  <span className="font-semibold text-white tabular-nums">{formatDayTimeBrt(f.t)}</span>
                  <span className="text-ink-400">{formatRelative(f.t, now)}</span>
                </span>
                <span className="block truncate text-xs text-ink-200">
                  {place ? (
                    <>
                      {place}
                      {estimated && <span className="text-ink-400"> (estimado)</span>}
                    </>
                  ) : (
                    <span className="text-ink-400">{formatLonLat(f.lon, f.lat, 2)}</span>
                  )}
                </span>
              </span>
              <span className="shrink-0 text-right text-[11px] leading-tight text-ink-300">
                <span className="block">{satelliteShort(f.satelite)}</span>
                {f.frp != null && <span className="block text-ink-400 tabular-nums">{formatDecimal(f.frp, 1)} MW</span>}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

const SHOWN_LABEL: Record<DisplayMode, string> = {
  frame: 'No quadro exibido',
  accum: 'Até o quadro exibido',
  all: 'No período todo',
};

/**
 * Card inferior: focos no período do loop (e na janela do modo atual), das
 * áreas do filtro ou de todo o conjunto. A contagem é por busca binária; o
 * seletor devolve só números, então o card só renderiza quando eles mudam
 * (não a cada quadro).
 */
function FireCountCard(props: { dataset: FireDataset; areas: AreaFilterStatus }) {
  const { dataset, areas } = props;
  const mode = useFireStore((s) => s.mode);
  const sources = useFireStore((s) => s.sources);
  const filtered = areas.state === 'ready';
  // Enquanto os limites baixam, o conjunto ainda é o do filtro anterior: não mostra número errado.
  const pending = areas.state === 'loading';

  const c = useTimeStore(
    useShallow((s) => {
      if (pending) return null;
      const snap = displayedSnapshot(s);
      const period = loopPeriod(snap.frames, snap.range);
      const w = windowFor(mode, snap);
      if (!period || !w) return null;
      const count = (from: number, to: number) => countWindow(dataset, from, to);
      const p = count(period.from, period.to);
      return {
        total: p.total,
        goes: p.goes,
        others: p.others,
        shown: mode === 'all' ? p.total : count(w.from, w.to).total,
        // Início exclusivo do período → primeiro quadro (o que o usuário vê na barra de tempo).
        from: snap.frames[0] ?? period.from,
        to: period.to,
      };
    }),
  );

  if (!sources.goes10min && !sources.diario) return null;

  return (
    <section
      className="border-t border-ink-700/70 bg-ink-850 px-3 py-2"
      aria-label="Contador de focos"
      aria-live="polite"
      data-testid="fire-count-card"
    >
      <div className="flex items-baseline gap-2">
        <Flame className="size-3.5 shrink-0 self-center text-fogo-400" aria-hidden />
        <span className="min-w-0 flex-1 text-xs text-ink-200">
          Focos no período do loop
          <span className="block truncate text-[11px] text-ink-400 tabular-nums">
            {c && (
              <span title={`${formatDayTimeBrt(c.from)} a ${formatDayTimeBrt(c.to)} (Brasília)`}>
                {formatTimeBrt(c.from)} → {formatTimeBrt(c.to)}
              </span>
            )}
            {c && filtered && ' · '}
            {filtered && <span className="text-fogo-400">nas áreas do filtro</span>}
          </span>
        </span>
        <strong className="text-lg leading-none font-semibold text-white tabular-nums" data-testid="fire-count-total">
          {c ? formatInt(c.total) : '—'}
        </strong>
      </div>
      {c && (
        <div className="mt-1 flex flex-wrap justify-between gap-x-3 pl-5.5 text-[11px] text-ink-300 tabular-nums">
          <span>
            {sources.diario ? (
              <>
                GOES-19: {formatInt(c.goes)} · outros: {formatInt(c.others)}
              </>
            ) : (
              <>só GOES-19</>
            )}
          </span>
          {mode !== 'all' && (
            <span>
              {SHOWN_LABEL[mode]}: <span className="text-ink-100">{formatInt(c.shown)}</span>
            </span>
          )}
        </div>
      )}
    </section>
  );
}

function ListFootnote(props: { filtered: boolean }) {
  return (
    <p className="border-t border-ink-700/70 px-3 py-1.5 text-[11px] leading-snug text-ink-400">
      Até {formatInt(RECENT_LIMIT)} focos, mais novos primeiro · horário de Brasília · mesmos filtros do mapa
      {props.filtered && ' · só nas áreas escolhidas'}
    </p>
  );
}

/** Filtro por áreas + lista rolável + contador + nota (inspetor e folha do celular). */
function RecentFiresBody(props: { onPick: (item: RecentFire) => void }) {
  const dataset = useFireStore((s) => s.dataset);
  const areas = useFireStore((s) => s.areaStatus);
  return (
    <>
      <AreaFilter missing={areas.missing} />
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <RecentFireList dataset={dataset} areas={areas} onPick={props.onPick} />
      </div>
      <FireCountCard dataset={dataset} areas={areas} />
      <ListFootnote filtered={areas.state === 'ready'} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Desktop e celular
// ---------------------------------------------------------------------------

/** Conteúdo do inspetor (desktop): lista rolável + nota de rodapé. */
export function RecentFiresInspector() {
  const map = useMapInstance((s) => s.map);
  return <RecentFiresBody onPick={(item) => map && goToFire(map, item)} />;
}

function MobileRecentFires(props: { map: OlMap }) {
  const [open, setOpen] = useState(false);
  const layerRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  useModalLayer(open, { onEscape: () => setOpen(false), ref: layerRef });
  useEffect(() => {
    if (open) sheetRef.current?.focus();
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="absolute right-[calc(8px+env(safe-area-inset-right))] z-10 inline-flex size-9 items-center justify-center rounded-xl border border-ink-600/90 bg-ink-900/95 text-ink-100 shadow-lg pointer-coarse:size-11 focus-visible:outline-2 focus-visible:outline-mata-400"
        // Abaixo de "voltar à Amazônia" (44 px) e de "⋯" (44 px), com 8 px entre eles.
        style={{ top: 'calc(env(safe-area-inset-top) + 12px + 44px + 8px + 44px + 8px)' }}
        aria-label="Últimos focos"
        title="Últimos focos"
        // Parte da coluna que a ficha do mapa não cobre (map/popupArea).
        data-coluna-do-mapa=""
      >
        <Flame className="size-4 text-fogo-400" aria-hidden />
      </button>
      {open &&
        // Portal para o <body> (fora de #root, que fica `inert` com a folha aberta), como a MobileSheet.
        createPortal(
          <div ref={layerRef}>
            <div className="fixed inset-0 z-40 bg-ink-950/30" onClick={() => setOpen(false)} aria-hidden />
            <div
              ref={sheetRef}
              role="dialog"
              aria-modal="true"
              aria-label="Últimos focos"
              tabIndex={-1}
              data-testid="recent-fires"
              className="fixed inset-x-0 bottom-0 z-50 flex max-h-[62dvh] flex-col rounded-t-2xl border-t border-ink-600 bg-ink-900 pb-[env(safe-area-inset-bottom)] shadow-2xl outline-none"
            >
              <div className="flex items-center gap-1.5 border-b border-ink-700/70 py-1.5 pr-2 pl-3">
                <Flame className="size-4 shrink-0 text-fogo-400" aria-hidden />
                <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-ink-100">Últimos focos</h2>
                <IconButton icon={X} label="Fechar" size="sm" onClick={() => setOpen(false)} className="pointer-coarse:size-11" />
              </div>
              <RecentFiresBody
                onPick={(item) => {
                  setOpen(false);
                  goToFire(props.map, item);
                }}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/** Montado pelo App sobre o mapa (dentro do <main>), só no celular; no desktop a lista vive no inspetor. */
export function RecentFires() {
  const map = useMapInstance((s) => s.map);
  if (!map) return null;
  return <MobileRecentFires map={map} />;
}
